<template>
  <div class="mt-6">
    <span class="secondary_color text-[13px]">When to start this step</span>
    <a-radio-group
      v-model:value="modelValue.timing_type"
      class="mt-3 flex flex-col"
    >
      <div class="flex flex-col">
        <a-radio
          :value="timingTypes.IMMEDIATE"
          class="config-section mb-2 flex items-start"
        >
          <span class="text-[14px] text-sm text_color">
            {{ isFirstStep ? immediateText.first : immediateText.subsequent }}
          </span>
        </a-radio>
      </div>
      <div class="flex flex-row mt-2 w-full items-center">
        <a-radio
          :value="timingTypes.SCHEDULE"
          class="config-section mb-1 flex items-start"
        ></a-radio>
        <div class="flex flex-row justify-start items-center gap-2 mb-1">
          <a-input
            type="number"
            :min="0"
            class="input_medium w-[80px] h-[32px]"
            v-model:value="modelValue.delay_time"
          />
          <a-select
            v-model:value="modelValue.delay_time_unit"
            class="w-[100px]"
          >
            <template #suffixIcon>
              <ThemeImage
                :icon="imageNames.BOTTOM_ARROW"
                alt="bottom_white_arrow"
                class="size-4"
              />
            </template>
            <a-select-option
              v-for="time in timeMap"
              :key="time.value"
              :value="time.value"
            >
              {{ time.label }}
            </a-select-option>
          </a-select>
          <span class="text-[14px] text_color">
            {{ isFirstStep ? scheduleText.first : scheduleText.subsequent }}
          </span>
        </div>
      </div>
    </a-radio-group>

    <div
      v-if="
        modelValue.timing_type === timingTypes.SCHEDULE &&
        modelValue.delay_time_unit === delayTimeUnits.DAYS &&
        specificExecutionTimeEnabled
      "
      class="mt-3 ml-6"
    >
      <div class="flex items-center gap-2">
        <a-checkbox v-model:checked="localSpecificTimeEnabled">
          <span class="text-[14px] text-sm text_color"
            >Specific time of the day</span
          >
        </a-checkbox>
      </div>

      <div v-if="localSpecificTimeEnabled" class="mt-2">
        <a-select
          v-model:value="localSpecificTime"
          class="w-full"
          placeholder="11:00AM"
          :loading="timeOptionsLoading"
          show-search
          :dropdown-match-select-width="false"
          @change="handleTimeChange"
        >
          <template #suffixIcon>
            <ThemeImage
              :icon="imageNames.BOTTOM_ARROW"
              alt="bottom_white_arrow"
              class="size-4"
            />
          </template>
          <a-select-option
            v-for="time in businessHourTimeOptions"
            :key="time"
            :value="time"
          >
            {{ time }}
          </a-select-option>
        </a-select>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  timeMap,
  sequenceStepTypes,
  timingTypes,
  delayTimeUnits,
  defaultTimes,
} from "@/src/constants/sequenceConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { ref, computed, onMounted, watch } from "vue";
import DashboardService from "@/src/api/DashboardService.js";
import SequenceService from "@/src/api/SequenceService.js";
import { generateTimeOptions } from "@/src/utils/utils.js";
import {
  convertToHHMM,
  convertToDisplayFormat,
  generateBusinessHourTimeOptions,
} from "@/src/utils/dateTimeUtils.js";
import dayjs from "dayjs";
import { featureNames } from "@/src/constants/globalConstants";

const specificExecutionTimeEnabled = computed(() => {
  return !!localStorage.getItem("sequences_specific_execution_time");
});

const props = defineProps({
  modelValue: { type: Object, required: true },
  isFirstStep: { type: Boolean, default: false },
  stepType: { type: String },
  sequenceId: { type: [String, Number], required: false },
});

const emit = defineEmits(["update:modelValue"]);

const STEP_SCHEDULE_BUFFER_TIME = 1;
const teamMembers = ref([]);
const teamMembersLoading = ref(false);
const sequenceSettings = ref(null);
const timeOptionsLoading = ref(false);
const businessHourTimeOptions = ref([]);

const localAssignTeamMember = computed({
  get: () => {
    return props.modelValue.assign_team_member !== undefined
      ? props.modelValue.assign_team_member
      : !!props.modelValue.assigned_to_id;
  },
  set: (value) => {
    const updatedValue = { ...props.modelValue, assign_team_member: value };
    if (!value) {
      updatedValue.assigned_to_id = null;
    }

    emit("update:modelValue", updatedValue);
  },
});

const localSpecificTimeEnabled = computed({
  get: () => {
    return !!props.modelValue.specific_execution_time;
  },
  set: (value) => {
    const updatedValue = { ...props.modelValue };
    if (!value) {
      updatedValue.specific_execution_time = null;
    } else {
      const defaultTime =
        businessHourTimeOptions.value.length > 0
          ? businessHourTimeOptions.value[0]
          : defaultTimes.START_TIME;
      updatedValue.specific_execution_time = convertToHHMM(defaultTime);
    }
    emit("update:modelValue", updatedValue);
  },
});

const localSpecificTime = computed({
  get: () => {
    if (!props.modelValue.specific_execution_time) return null;
    return convertToDisplayFormat(props.modelValue.specific_execution_time);
  },
  set: (value) => {
    if (!value) return;
    const updatedValue = {
      ...props.modelValue,
      specific_execution_time: convertToHHMM(value),
    };
    emit("update:modelValue", updatedValue);
  },
});

const immediateText = computed(() => ({
  first: `Immediately after the person is added to ${featureNames.SEQUENCES}`,
  subsequent: "Immediately after the previous step",
}));

const scheduleText = computed(() => ({
  first: "after the person is added",
  subsequent: "after the previous step",
}));

const isLinkedInStepType = computed(() => {
  return [
    sequenceStepTypes.LINKEDIN_MESSAGE,
    sequenceStepTypes.LINKEDIN_INVITE,
    sequenceStepTypes.LINKEDIN_INMAIL,
  ].includes(props.stepType);
});

const userConnectionStatuses = computed(() => {
  return teamMembers.value.reduce((acc, user) => {
    acc[user.id] = getUserConnectionStatus(user);
    return acc;
  }, {});
});

const generateBusinessHourTimeOptionsWithBuffer = (startTime, endTime) => {
  return generateBusinessHourTimeOptions(
    startTime,
    endTime,
    STEP_SCHEDULE_BUFFER_TIME,
    generateTimeOptions,
  );
};

const fetchSequenceSettings = async (forceRefresh = false) => {
  if (!props.sequenceId) {
    businessHourTimeOptions.value = generateBusinessHourTimeOptionsWithBuffer();
    return;
  }

  if (timeOptionsLoading.value && !forceRefresh) return;

  timeOptionsLoading.value = true;
  try {
    const response = await SequenceService.fetchSequenceSettings(
      props.sequenceId,
    );
    if (response?.success) {
      sequenceSettings.value = response.settings;

      const startTime = sequenceSettings.value.start_time
        ? convertToDisplayFormat(sequenceSettings.value.start_time)
        : defaultTimes.START_TIME;
      const endTime = sequenceSettings.value.end_time
        ? convertToDisplayFormat(sequenceSettings.value.end_time)
        : defaultTimes.END_TIME;

      businessHourTimeOptions.value = generateBusinessHourTimeOptionsWithBuffer(
        startTime,
        endTime,
      );

      if (
        props.modelValue.specific_execution_time &&
        localSpecificTimeEnabled.value
      ) {
        const currentDisplayTime = convertToDisplayFormat(
          props.modelValue.specific_execution_time,
        );
        if (!businessHourTimeOptions.value.includes(currentDisplayTime)) {
          const updatedValue = {
            ...props.modelValue,
            specific_execution_time: null,
          };
          emit("update:modelValue", updatedValue);
        }
      }
    }
  } catch (error) {
    console.error("Error fetching sequence settings:", error);
    businessHourTimeOptions.value = generateBusinessHourTimeOptionsWithBuffer();
  } finally {
    timeOptionsLoading.value = false;
  }
};

const fetchTeamMembers = async () => {
  if (teamMembersLoading.value) return;

  teamMembersLoading.value = true;
  try {
    const response = await DashboardService.fetchUsers("active");
    if (response.success) {
      teamMembers.value = response.users;
    }
  } catch (error) {
    console.error("Error fetching team members:", error);
  } finally {
    teamMembersLoading.value = false;
  }
};

const handleTeamMemberChange = (value) => {
  const updatedValue = { ...props.modelValue, assigned_to_id: value ?? null };
  emit("update:modelValue", updatedValue);
};

const handleTimeChange = (value) => {
  if (!value) return;

  const updatedValue = {
    ...props.modelValue,
    specific_execution_time: convertToHHMM(value),
  };
  emit("update:modelValue", updatedValue);
};

const filterTeamMembers = (input, option) => {
  return option.label.toLowerCase().includes(input.toLowerCase());
};

const getUserConnectionStatus = (user) => {
  if (props.stepType === sequenceStepTypes.EMAIL && !user.email_connected) {
    return "Email not connected";
  }
  if (isLinkedInStepType.value && !user.linkedin_connected) {
    return "LinkedIn not connected";
  }
  return null;
};

watch(
  () => [props.modelValue.timing_type, props.modelValue.delay_time_unit],
  ([newTimingType, newDelayTimeUnit]) => {
    if (
      newTimingType !== timingTypes.SCHEDULE ||
      newDelayTimeUnit !== delayTimeUnits.DAYS
    ) {
      if (props.modelValue.specific_execution_time) {
        const updatedValue = {
          ...props.modelValue,
          specific_execution_time: null,
        };
        emit("update:modelValue", updatedValue);
      }
    }
  },
);

const refreshTimeOptions = () => {
  if (specificExecutionTimeEnabled.value) {
    fetchSequenceSettings(true);
  }
};

defineExpose({
  refreshTimeOptions,
});

onMounted(() => {
  fetchTeamMembers();
  if (specificExecutionTimeEnabled.value) {
    fetchSequenceSettings();
  }
});
</script>
